import type { ReactNode } from "react";
import { Search } from "lucide-react";
import { useUIStore } from "@/store/ui";

interface Props {
  title: string;
  subtitle?: ReactNode;
  /** When set, shows a filter box that drives the page's search. */
  onSearch?: (q: string) => void;
  searchPlaceholder?: string;
  /** Right-aligned actions (primary button, overflow menu…). */
  actions?: ReactNode;
}

/** Page header: title, optional search, and the page's actions. */
export default function TopBar({ title, subtitle, onSearch, searchPlaceholder, actions }: Props) {
  const openCmdK = useUIStore((s) => s.openCmdK);

  return (
    <div className="sticky top-12 md:top-0 z-30 border-b border-fg/[0.06] bg-ink-950/80 backdrop-blur-md">
      <div className="px-4 md:px-6 py-3 md:py-3.5 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 md:flex-none pr-2">
          <h1 className="text-lg font-semibold tracking-tight leading-tight">{title}</h1>
          {subtitle && <div className="hidden sm:block text-sm text-mut-500">{subtitle}</div>}
        </div>

        <div className="hidden md:block flex-1" />

        {onSearch ? (
          <div className="relative order-last md:order-none w-full md:max-w-[360px] min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-mut-500 pointer-events-none" />
            <input
              type="search"
              placeholder={searchPlaceholder ?? "Filter…"}
              className="input pl-9 py-1.5"
              onChange={(e) => onSearch(e.target.value)}
              aria-label={searchPlaceholder ?? "Filter"}
            />
          </div>
        ) : (
          <button
            onClick={openCmdK}
            title="Search & jump (⌘K)"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-[10px] border border-fg/[0.1] bg-ink-900 text-sm text-mut-500 hover:text-mut-300"
          >
            <Search className="w-3.5 h-3.5" />
            Search
            <span className="kbd">⌘K</span>
          </button>
        )}

        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}
